// The suggested supplier rule for a document whose supplier has none (0035).
//
// Shown only when the reading stopped at SUPPLIER_NOT_MAPPED. It says who the
// supplier most likely is in the accounting package and why — the VAT or KvK
// number they share, never a name — and which cost ledger that relation's past
// purchase invoices were booked to. Nothing is applied by showing it: the rule
// is saved only when a person presses the button, and then the document is
// read again with it. Relations are never created here.

import { useState, type ReactNode } from 'react';
import type { SupplierSuggestion as Suggestion } from '../api/types.ts';
import { useT, type MessageKey } from '../i18n/index.tsx';

const MATCHED_ON: Readonly<Record<'vat_number' | 'registration_number', MessageKey>> = {
  vat_number: 'source.suggest.matchedOn.vat_number',
  registration_number: 'source.suggest.matchedOn.registration_number',
};

function ledgerLabel(ledger: { code: string; name: string | null }): string {
  return ledger.name === null ? ledger.code : `${ledger.code} ${ledger.name}`;
}

export function SupplierSuggestion({
  suggestion,
  working,
  onConfirm,
}: {
  readonly suggestion: Suggestion;
  readonly working: boolean;
  readonly onConfirm: (relationId: string, ledgerId: string, vatDeductible: boolean) => void;
}): ReactNode {
  const t = useT();
  const top = suggestion.kind === 'suggestion' ? suggestion.ledgers[0] : undefined;
  // Pre-selected only when the history suggests one; otherwise the person picks.
  const [ledgerId, setLedgerId] = useState<string>(top?.ledgerId ?? '');
  // Never ticked by default: non-deductible VAT is the office's call.
  const [nonDeductible, setNonDeductible] = useState(false);

  if (suggestion.kind === 'none') {
    return (
      <div className="suggest" data-testid="supplier-suggestion">
        <b>{t('source.suggest.noneTitle')}</b>
        <p className="muted small">{t('source.suggest.noneBody')}</p>
      </div>
    );
  }
  if (suggestion.kind === 'ambiguous') {
    return (
      <div className="suggest" data-testid="supplier-suggestion">
        <b>
          {t('source.suggest.ambiguousTitle', {
            count: suggestion.candidates,
            matchedOn: t(MATCHED_ON[suggestion.matchedOn]),
          })}
        </b>
        <p className="muted small">{t('source.suggest.ambiguousBody')}</p>
      </div>
    );
  }

  const relation = suggestion.relation;
  const identifier = suggestion.matchedOn === 'vat_number' ? relation.vatNumber : relation.registrationNumber;
  const chosen = suggestion.chart.find((ledger) => ledger.ledgerId === ledgerId);
  const history = suggestion.ledgers.find((ledger) => ledger.ledgerId === ledgerId);
  const matchedOn = t(MATCHED_ON[suggestion.matchedOn]);
  const read = suggestion.invoicesRead;
  return (
    <div className="suggest" data-testid="supplier-suggestion">
      <div className="suggest-head">
        <b>{t('source.suggest.title')}</b>
        <span className="badge ai">{t('source.suggest.badge')}</span>
      </div>
      <p className="small">
        {t('source.suggest.relationBefore')}
        <b>{[relation.code, relation.name].filter(Boolean).join(' · ') || relation.id}</b>
        {t('source.suggest.relationAfter', { matchedOn, identifier: identifier ?? '' })}
      </p>
      <label className="field">
        {t('source.suggest.costLedger')}
        <select
          value={ledgerId}
          onChange={(event) => setLedgerId(event.target.value)}
          aria-label={t('source.suggest.costLedger')}
        >
          {top === undefined ? <option value="">{t('source.suggest.chooseLedger')}</option> : null}
          {suggestion.ledgers.length === 0 ? null : (
            <optgroup label={t('source.suggest.usedBefore')}>
              {suggestion.ledgers.map((ledger) => (
                <option key={ledger.ledgerId} value={ledger.ledgerId}>
                  {t('source.suggest.ledgerUse', { ledger: ledgerLabel(ledger), invoices: ledger.invoices, read })}
                </option>
              ))}
            </optgroup>
          )}
          <optgroup label={t('source.suggest.chart')}>
            {suggestion.chart.map((ledger) => (
              <option key={ledger.ledgerId} value={ledger.ledgerId}>
                {ledgerLabel(ledger)}
              </option>
            ))}
          </optgroup>
        </select>
      </label>
      <p className="muted small" data-testid="suggestion-basis">
        {read === 0
          ? t('source.suggest.noHistory')
          : suggestion.ledgers.length === 0
            ? t('source.suggest.noneInChart', { read })
            : history === undefined
              ? t('source.suggest.notUsed', { read })
              : t('source.suggest.usedOn', { invoices: history.invoices, read })}
      </p>
      <label className="check-row small">
        <input type="checkbox" checked={nonDeductible} onChange={(event) => setNonDeductible(event.target.checked)} />
        {t('source.suggest.nonDeductible')}
      </label>
      <div className="flex">
        <button
          type="button"
          className="btn primary"
          disabled={working || chosen === undefined}
          onClick={() => onConfirm(relation.id, ledgerId, !nonDeductible)}
        >
          {t('source.suggest.use')}
        </button>
        <span className="muted small">
          {t('source.suggest.useNote', { matchedOn })}
        </span>
      </div>
    </div>
  );
}
